import React, { useState, useMemo } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAIWorkflow } from "../contexts/AIWorkflowContext";
import JobSearchResultsAI from "../components/dashboard/ai_flow/JobSearchResultsAI";
import JobDetails from "../components/dashboard/jobs/JobDetails";
import jobService from "../services/jobService";
import "../components/dashboard/jobs/JobMatcher.css";

const canadianProvincesAndTerritories = [
  { name: "All of Canada", value: "Canada" },
  { name: "Alberta", value: "Alberta, Canada" },
  { name: "British Columbia", value: "British Columbia, Canada" },
  { name: "Manitoba", value: "Manitoba, Canada" },
  { name: "New Brunswick", value: "New Brunswick, Canada" },
  {
    name: "Newfoundland and Labrador",
    value: "Newfoundland and Labrador, Canada",
  },
  { name: "Nova Scotia", value: "Nova Scotia, Canada" },
  { name: "Ontario", value: "Ontario, Canada" },
  { name: "Prince Edward Island", value: "Prince Edward Island, Canada" },
  { name: "Quebec", value: "Quebec, Canada" },
  { name: "Saskatchewan", value: "Saskatchewan, Canada" },
  { name: "Northwest Territories", value: "Northwest Territories, Canada" },
  { name: "Nunavut", value: "Nunavut, Canada" },
  { name: "Yukon", value: "Yukon, Canada" },
];

// Instructions Component
const OptimizationInstructions = () => {
  return (
    <div className="optimization-instructions mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <div className="flex items-start gap-3">
        <div className="text-blue-600 text-xl mt-1">💡</div>
        <div className="flex-1">
          <h4 className="font-semibold text-blue-900 mb-1">
            How to optimize your resume:
          </h4>
          <p className="text-sm text-blue-800">
            <strong>Click on any job card</strong> to start tailoring your
            resume for that specific position. Or click{" "}
            <strong>"View Job Details"</strong> to see more information about
            the role.
          </p>
        </div>
      </div>
    </div>
  );
};

// Optimization Confirmation Modal
const OptimizationModal = ({ isOpen, onClose, onConfirm, job }) => {
  if (!isOpen || !job) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6 animate-fadeIn">
        <div className="text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Optimize Resume for This Position?
          </h3>

          <div className="mb-6 text-left bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">
              <strong className="text-gray-900">{job.jobTitle}</strong>
            </p>
            <p className="text-sm text-gray-600">
              {job.companyName} • {job.location}
            </p>
          </div>

          <p className="text-sm text-gray-600 mb-6">
            Would you like to tailor your resume specifically for this position?
            Our AI will customize your resume to match the job requirements.
          </p>

          {/* Buttons */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              No, Stay Here
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 text-white rounded-lg transition-colors font-medium"
              style={{ backgroundColor: "rgb(6, 95, 70)" }}
              onMouseEnter={(e) =>
                (e.target.style.backgroundColor = "rgb(5, 75, 55)")
              }
              onMouseLeave={(e) =>
                (e.target.style.backgroundColor = "rgb(6, 95, 70)")
              }
            >
              Yes, Optimize Resume
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Province Search Component - Updated
const ProvinceSearchSection = ({
  selectedCategory,
  currentLocation,
  onNewLocationSearch,
  isSearching,
}) => {
  const [selectedProvince, setSelectedProvince] = useState(
    currentLocation || "Canada"
  );
  const [showSearch, setShowSearch] = useState(false);

  const handleSearch = () => {
    if (selectedProvince && selectedProvince !== currentLocation) {
      onNewLocationSearch(selectedProvince);
    }
  };

  const currentProvinceName =
    canadianProvincesAndTerritories.find((p) => p.value === currentLocation)
      ?.name || currentLocation;

  const selectedProvinceName =
    canadianProvincesAndTerritories.find((p) => p.value === selectedProvince)
      ?.name || selectedProvince;

  const isButtonDisabled =
    !selectedProvince || selectedProvince === currentLocation || isSearching;

  return (
    <div
      className="province-search-section"
      style={{
        marginBottom: "24px",
        padding: "24px",
        backgroundColor: "#ffffff",
        borderRadius: "16px",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
        border: "1px solid #e5e7eb",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: showSearch ? "20px" : "0",
        }}
      >
        <div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "600",
              color: "#111827",
              margin: "0 0 8px 0",
            }}
          >
            Search Different Province
          </h3>
          <p
            style={{
              fontSize: "14px",
              color: "#6b7280",
              margin: "0",
            }}
          >
            Currently showing results for:{" "}
            <span
              style={{
                fontWeight: "600",
                color: "rgb(6, 95, 70)",
              }}
            >
              {currentProvinceName}
            </span>
          </p>
        </div>
        <button
          onClick={() => setShowSearch(!showSearch)}
          style={{
            padding: "8px 16px",
            fontSize: "14px",
            fontWeight: "500",
            backgroundColor: "#f3f4f6",
            color: "#374151",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#e5e7eb";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#f3f4f6";
          }}
        >
          {showSearch ? "Hide" : "Change"} Province
        </button>
      </div>

      {showSearch && (
        <div
          style={{
            animation: "fadeIn 0.3s ease-in-out",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginBottom: "16px",
              flexWrap: "wrap",
            }}
          >
            <select
              value={selectedProvince}
              onChange={(e) => setSelectedProvince(e.target.value)}
              style={{
                flex: "1",
                minWidth: "200px",
                padding: "12px 16px",
                fontSize: "15px",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                backgroundColor: "#ffffff",
                color: "#111827",
                cursor: "pointer",
                outline: "none",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "rgb(6, 95, 70)";
                e.target.style.boxShadow = "0 0 0 3px rgba(6, 95, 70, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#e5e7eb";
                e.target.style.boxShadow = "none";
              }}
            >
              {canadianProvincesAndTerritories.map((province) => (
                <option key={province.value} value={province.value}>
                  {province.name}
                </option>
              ))}
            </select>

            <button
              onClick={handleSearch}
              disabled={isButtonDisabled}
              style={{
                padding: "12px 32px",
                fontSize: "15px",
                fontWeight: "600",
                backgroundColor: isButtonDisabled
                  ? "#e5e7eb"
                  : "rgb(6, 95, 70)",
                color: isButtonDisabled ? "#9ca3af" : "#ffffff",
                border: "none",
                borderRadius: "8px",
                cursor: isButtonDisabled ? "not-allowed" : "pointer",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
              onMouseEnter={(e) => {
                if (!isButtonDisabled) {
                  e.target.style.backgroundColor = "rgb(5, 75, 55)";
                  e.target.style.transform = "translateY(-1px)";
                  e.target.style.boxShadow = "0 4px 12px rgba(6, 95, 70, 0.3)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isButtonDisabled) {
                  e.target.style.backgroundColor = "rgb(6, 95, 70)";
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "none";
                }
              }}
            >
              {isSearching ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      animation: "spin 1s linear infinite",
                    }}
                  >
                    ⟳
                  </span>
                  Searching...
                </span>
              ) : (
                "Search Jobs"
              )}
            </button>
          </div>

          {/* Selected Province Indicator */}
          {selectedProvince !== currentLocation && (
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
                marginBottom: "12px",
              }}
            >
              <p
                style={{
                  fontSize: "14px",
                  color: "#166534",
                  margin: "0",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <span style={{ fontSize: "16px" }}>→</span>
                You've selected: <strong>{selectedProvinceName}</strong>
              </p>
            </div>
          )}

          {/* Category Note */}
          {selectedCategory && (
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "14px",
                color: "#4b5563",
              }}
            >
              <span style={{ fontWeight: "600", color: "#111827" }}>Note:</span>{" "}
              This will search for new{" "}
              <span
                style={{
                  fontWeight: "600",
                  color: "rgb(6, 95, 70)",
                }}
              >
                {selectedCategory.job_category}
              </span>{" "}
              positions in{" "}
              <span style={{ fontWeight: "600" }}>
                {selectedProvince === currentLocation
                  ? "the same location"
                  : selectedProvinceName}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Enhanced Loading Component that matches AIMatchmakingPage style
const SearchingForOpportunities = ({ selectedCategory }) => {
  return (
    <div
      className="job-search-loading"
      style={{
        background: "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(10px)",
        borderRadius: "24px",
        padding: "3rem",
        textAlign: "center",
        boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(255, 255, 255, 0.2)",
        margin: "2rem auto",
        maxWidth: "600px",
      }}
    >
      <div
        className="loading-spinner"
        style={{
          width: "60px",
          height: "60px",
          border: "4px solid #e2e8f0",
          borderTop: "4px solid rgb(6, 95, 70)",
          borderRadius: "50%",
          animation: "spin 1s linear infinite",
          margin: "0 auto 1.5rem",
        }}
      />
      <h3
        style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#2d3748",
          marginBottom: "1rem",
        }}
      >
        Searching for Opportunities
      </h3>
      <p
        style={{
          fontSize: "1.1rem",
          color: "#718096",
          marginBottom: "0.5rem",
        }}
      >
        Analyzing your resume and matching with available positions...
      </p>
      <p
        style={{
          fontSize: "0.9rem",
          color: "#a0aec0",
        }}
      >
        This may take a moment while we find the best matches for you.
      </p>
    </div>
  );
};

function JobListingAIPage() {
  const navigate = useNavigate();

  const {
    aiMatchedJobs,
    setAiMatchedJobs,
    paginationInfo,
    setPaginationInfo,
    isLoadingAiJobs,
    aiJobsError,
    pollingForJobs,
    setSelectedJobForOptimization,
    selectedJobForOptimization,
    aiTaskId,
    masterResume,
    finalResumeContentForJobSearch,
    setFinalResumeContentForJobSearch,
    flowShouldSkipResumeBuilder,
    selectedCategory,
    userLocationInput,
    searchJobsForCategory,
    optimizedResume,
    structuredResumeData,
    setStructuredResumeData,
    setOptimizedResume,
  } = useAIWorkflow();

  // Debug logging
  console.log("[JobListingAIPage] Component rendered with:", {
    hasAiTaskId: !!aiTaskId,
    hasMasterResume: !!masterResume,
    masterResumeLength: masterResume?.length,
    hasFinalResumeContent: !!finalResumeContentForJobSearch,
    finalResumeLength: finalResumeContentForJobSearch?.length,
    flowShouldSkipResumeBuilder,
    jobCount: aiMatchedJobs.length,
    isLoadingAiJobs,
    pollingForJobs,
  });

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [jobDetailsForModal, setJobDetailsForModal] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isSearchingNewLocation, setIsSearchingNewLocation] = useState(false);
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);
  const [pendingOptimizationJob, setPendingOptimizationJob] = useState(null);

  const shouldRedirect = useMemo(() => {
    if (isLoadingAiJobs || pollingForJobs || aiTaskId) return false;
    if (flowShouldSkipResumeBuilder && !finalResumeContentForJobSearch)
      return true;
    if (
      !aiTaskId &&
      !isLoadingAiJobs &&
      !pollingForJobs &&
      !finalResumeContentForJobSearch &&
      !masterResume
    )
      return true;
    return false;
  }, [
    isLoadingAiJobs,
    pollingForJobs,
    aiTaskId,
    masterResume,
    finalResumeContentForJobSearch,
    flowShouldSkipResumeBuilder,
  ]);

  if (shouldRedirect) {
    return <Navigate to="/dashboard/ai-matchmaking" replace />;
  }

  const handleNewLocationSearch = async (newLocation) => {
    if (!selectedCategory) {
      alert(
        "No career category selected. Please go back and select a career path."
      );
      return;
    }

    if (!searchJobsForCategory) {
      console.error("searchJobsForCategory function not available in context");
      return;
    }

    setIsSearchingNewLocation(true);

    try {
      console.log(
        `Searching for ${selectedCategory.job_category} jobs in ${newLocation}`
      );

      const success = await searchJobsForCategory(
        selectedCategory,
        newLocation
      );

      if (success) {
        console.log(
          `Successfully initiated search for ${selectedCategory.job_category} jobs in ${newLocation}`
        );
      } else {
        throw new Error("Search failed");
      }
    } catch (error) {
      console.error("Failed to search new location:", error);
      alert("Failed to search for jobs in the new location. Please try again.");
    } finally {
      setIsSearchingNewLocation(false);
    }
  };

  const handleLoadMore = async () => {
    if (
      isLoadingMore ||
      !paginationInfo ||
      paginationInfo.current_page >= paginationInfo.total_pages
    ) {
      return;
    }
    setIsLoadingMore(true);
    try {
      const nextPage = paginationInfo.current_page + 1;
      const result = await jobService.fetchJobResultsPage(
        aiTaskId,
        nextPage,
        10
      );
      setAiMatchedJobs((prevJobs) => [...prevJobs, ...result.jobs]);
      setPaginationInfo(result.pagination);
    } catch (error) {
      console.error("Failed to load more jobs:", error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Modified to show modal first
  const handleJobClick = (job) => {
    setPendingOptimizationJob(job);
    setShowOptimizationModal(true);
  };

  // Handle optimization confirmation with profile fallback
  const handleOptimizationConfirm = () => {
    if (!pendingOptimizationJob) return;

    // Since setOptimizedResume is not exposed in the context,
    // the clearing will happen automatically when we navigate to the optimization page
    // and it checks for a new job selection

    if (typeof setStructuredResumeData === "function") {
      setStructuredResumeData(null);
    }

    setSelectedJobForOptimization(pendingOptimizationJob);

    // Determine what content to use for optimization
    let contentToUseForOptimization = null;

    if (flowShouldSkipResumeBuilder) {
      // If we skipped resume builder, use the final resume content if available
      contentToUseForOptimization = finalResumeContentForJobSearch;
    } else {
      // Otherwise, prefer final resume content, fall back to master resume
      contentToUseForOptimization =
        finalResumeContentForJobSearch || masterResume;
    }

    // Check if we have valid content
    if (
      contentToUseForOptimization &&
      typeof contentToUseForOptimization === "string" &&
      contentToUseForOptimization.trim().length > 50
    ) {
      // We have valid content, proceed normally
      if (finalResumeContentForJobSearch !== contentToUseForOptimization) {
        setFinalResumeContentForJobSearch(contentToUseForOptimization);
      }
      navigate("/dashboard/resume-optimization");
    } else {
      // No valid resume content - we'll need to generate from profile
      console.log(
        "[JobListingAIPage] No base resume content available. Will use profile data for optimization."
      );

      // Set null to signal that profile should be used
      setFinalResumeContentForJobSearch(null);

      // Still navigate to the optimization page
      navigate("/dashboard/resume-optimization");
    }

    setShowOptimizationModal(false);
    setPendingOptimizationJob(null);
  };

  const handleOptimizationCancel = () => {
    setShowOptimizationModal(false);
    setPendingOptimizationJob(null);
  };

  const handleShowDetailsModal = (job) => {
    setJobDetailsForModal(job);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setJobDetailsForModal(null);
  };

  const canLoadMore =
    paginationInfo && paginationInfo.current_page < paginationInfo.total_pages;

  // Check if we should show the initial loading state
  // This happens when we're polling for jobs but don't have any results yet
  const showInitialLoading =
    (isLoadingAiJobs || pollingForJobs) &&
    aiMatchedJobs.length === 0 &&
    !isSearchingNewLocation;

  return (
    <div className="job-listing-ai-page p-4 md:p-6 lg:p-8">
      {/* Show the loading state if we're waiting for initial jobs */}
      {showInitialLoading ? (
        <SearchingForOpportunities selectedCategory={selectedCategory} />
      ) : (
        <>
          {/* Province Search Section */}
          <ProvinceSearchSection
            selectedCategory={selectedCategory}
            currentLocation={userLocationInput}
            onNewLocationSearch={handleNewLocationSearch}
            isSearching={
              isSearchingNewLocation || isLoadingAiJobs || pollingForJobs
            }
          />

          {/* Instructions Section */}
          {!isLoadingAiJobs && !pollingForJobs && aiMatchedJobs.length > 0 && (
            <OptimizationInstructions />
          )}

          {/* Job Results Section */}
          <JobSearchResultsAI
            jobs={aiMatchedJobs}
            isLoading={isSearchingNewLocation}
            error={aiJobsError}
            onSelectJob={handleJobClick}
            onShowDetailsModal={handleShowDetailsModal}
            selectedJobId={
              selectedJobForOptimization?.id ||
              selectedJobForOptimization?.job_link
            }
            canLoadMore={canLoadMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
          />
        </>
      )}

      {/* Job Details Modal */}
      {showDetailsModal && jobDetailsForModal && (
        <JobDetails
          job={jobDetailsForModal}
          onClose={handleCloseDetailsModal}
        />
      )}

      {/* Optimization Confirmation Modal */}
      <OptimizationModal
        isOpen={showOptimizationModal}
        onClose={handleOptimizationCancel}
        onConfirm={handleOptimizationConfirm}
        job={pendingOptimizationJob}
      />

      {/* Add CSS for animations */}
      <style jsx>{`
        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes pulse {
          0%,
          100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }

        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default JobListingAIPage;
